import React, { useState, useEffect } from "react";
import { Check, X, Clock, Printer } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert, AlertDescription } from "@/components/ui/Alert";
import { getPrinterService } from "../../../services/printer/MockPrinterService";
import { salesService } from "../../../services/salesService";

const ArqueoButtons = () => {
  const printerService = getPrinterService();
  const [isArqueoActive, setIsArqueoActive] = useState(false);
  const [lastActivation, setLastActivation] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [isPrinting, setIsPrinting] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);

  useEffect(() => {
    // Al montar el componente, cargar estado del arqueo y printers
    const loadInitialData = async () => {
      // Cargar estado del arqueo
      const savedState = localStorage.getItem("arqueoActive");
      if (savedState !== null) {
        setIsArqueoActive(JSON.parse(savedState));
      }

      // Recuperar la última fecha de activación
      const savedActivation = localStorage.getItem("arqueoLastActivation");
      if (savedActivation) {
        setLastActivation(new Date(savedActivation));
      }

      // Cargar printers disponibles
      try {
        const savedPrinters = localStorage.getItem("connectedPrinters");
        if (savedPrinters) {
          setAvailablePrinters(JSON.parse(savedPrinters));
        }
      } catch (error) {
        console.error("Error al cargar impresoras:", error);
      }
    };

    loadInitialData();
  }, []);

  const showTemporaryAlert = (message, type = "success") => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const handleActivateArqueo = () => {
    if (isArqueoActive) {
      showTemporaryAlert("El arqueo ya está activo", "warning");
      return;
    }

    setIsArqueoActive(true);
    const now = new Date();
    setLastActivation(now);

    // Guardar el nuevo estado en localStorage
    localStorage.setItem("arqueoActive", JSON.stringify(true));
    localStorage.setItem("arqueoLastActivation", now.toISOString());

    // Emitir evento para notificar a otros componentes
    const event = new CustomEvent("arqueoStateChanged", {
      detail: { isActive: true, activatedAt: now },
    });
    window.dispatchEvent(event);

    showTemporaryAlert("Arqueo activado correctamente");
  };

  const printArqueoReports = async () => {
    console.log("---- printArqueoReports ----")
    try {
      setIsPrinting(true);

      // Obtener los datos necesarios para la impresión
      const dateRange = {
        startDate: lastActivation || new Date(),
        endDate: new Date(),
        closingTime: 4, // Valor predeterminado
      };

      // Obtener datos solo con pagos válidos y del período de arqueo activo
      const [metrics, salesData] = await Promise.all([
        salesService.getMetrics(
          dateRange.startDate,
          dateRange.endDate,
          dateRange.closingTime,
          true, // Filtrar solo pagos válidos (Tarjeta/Efectivo)
          true // Filtrar solo órdenes con isArqueoActive=true
        ),
        salesService.getDailySales(
          dateRange.startDate,
          dateRange.endDate,
          dateRange.closingTime,
          true, // Filtrar solo pagos válidos (Tarjeta/Efectivo)
          true // Filtrar solo órdenes con isArqueoActive=true
        ),
      ]);
console.log("Obtener datos : ", metrics, " ---- ", salesData)
      // Verificar si hay impresoras disponibles
      if (!availablePrinters.length) {
        return showTemporaryAlert(
          "No hay impresoras disponibles para imprimir el arqueo",
          "warning"
        );
      }

      // Verificar si hay datos de ventas para imprimir
      if (
        !salesData?.data?.salesByDate?.transactions ||
        salesData.data.salesByDate.transactions.length === 0
      ) {
        showTemporaryAlert(
          "No hay ventas registradas con pagos válidos en el arqueo para imprimir",
          "warning"
        );
        return false;
      }

      // Seleccionar la primera impresora disponible
      const printerId = availablePrinters[0].id;
console.log("Previo a imprimir por servicio")
      // Imprimir los reportes en orden
   //   await printerService.printDailyReport(printerId, salesData);
      console.log("ArqueoButton: SalesData ----- ", salesData)
       console.log("ArqueoButton: Metrics ----- ", metrics)
      await printerService.printProductSummary(
        printerId,
        salesData.data.salesByDate,
        metrics
      );

      // Mostrar alerta de éxito
      showTemporaryAlert(
        "Reportes de arqueo impresos correctamente",
        "success"
      );

      return true;
    } catch (error) {
      console.error("Error al imprimir reportes de arqueo:", error);
      showTemporaryAlert(`Error al imprimir: ${error.message}`, "destructive");
      return false;
    } finally {
      setIsPrinting(false);
    }
  };

  const handleDeactivateArqueo = async () => {
    if (!isArqueoActive) {
      showTemporaryAlert("El arqueo ya está desactivado", "warning");
      return;
    }

    // Primero imprimir los reportes
    showTemporaryAlert("Imprimiendo reportes de arqueo...", "default");
    const printSuccess = await printArqueoReports();

    // Luego desactivar el arqueo
    setIsArqueoActive(false);

    // Guardar el nuevo estado en localStorage
    localStorage.setItem("arqueoActive", JSON.stringify(false));

    // Emitir evento para notificar a otros componentes
    const event = new CustomEvent("arqueoStateChanged", {
      detail: { isActive: false, printedReports: printSuccess },
    });
    window.dispatchEvent(event);

    showTemporaryAlert("Arqueo cerrado correctamente", "success");
  };

  const formatDateTime = (date) => {
    if (!date) return "No disponible";
    return new Intl.DateTimeFormat("es-ES", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };

  return (
    <Card className="border-orange-200 mb-6">
      <CardHeader className="border-b border-orange-200 bg-orange-500">
        <CardTitle className="text-2xl text-white flex items-center">
          <Clock className="mr-2 h-6 w-6" />
          Control de Arqueo
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {showAlert && (
          <Alert
            variant={alertType === "success" ? "default" : alertType}
            className={`mb-4 ${
              alertType === "success"
                ? "bg-green-50 border-green-200 text-green-700"
                : alertType === "warning"
                ? "bg-yellow-50 border-yellow-200 text-yellow-700"
                : ""
            }`}
          >
            <AlertDescription>{alertMessage}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col space-y-4">
          <div className="bg-white p-4 rounded-lg border border-orange-200">
            <h3 className="font-medium text-gray-900 mb-2">
              Estado del Arqueo
            </h3>
            <div className="flex items-center mb-4">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  isArqueoActive
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {isArqueoActive ? (
                  <>
                    <Check size={16} className="mr-1" />
                    Activo
                  </>
                ) : (
                  <>
                    <X size={16} className="mr-1" />
                    Inactivo
                  </>
                )}
              </span>
            </div>

            {lastActivation && (
              <p className="text-sm text-gray-500 mb-4">
                Último arqueo activado: {formatDateTime(lastActivation)}
              </p>
            )}

            <div className="flex space-x-4">
              <Button
                onClick={handleActivateArqueo}
                className="bg-green-500 hover:bg-green-600 text-white"
                disabled={isArqueoActive}
              >
                <Check className="mr-2 h-4 w-4" />
                Abrir Arqueo
              </Button>
              <Button
                onClick={handleDeactivateArqueo}
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={!isArqueoActive || isPrinting}
              >
                {isPrinting ? (
                  <Printer className="mr-2 h-4 w-4 animate-pulse" />
                ) : (
                  <X className="mr-2 h-4 w-4" />
                )}
                {isPrinting ? "Imprimiendo..." : "Cerrar Arqueo"}
              </Button>
            </div>

            {!availablePrinters.length && isArqueoActive && (
              <div className="mt-4 text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                <p className="flex items-center">
                  <Printer className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>
                    No hay impresoras configuradas. Al cerrar el arqueo no se
                    podrán imprimir los reportes.
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArqueoButtons;
